import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiSettings,
  FiClipboard,
  FiBox,
  FiUsers,
  FiBarChart2,
  FiTruck,
  FiCheckCircle,
} from "react-icons/fi";

const IndustryManufacturing = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Manufacturing Industry Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Manufacturing Industry | RedERP</title>
        <meta name="description" content="Discover how RedERP empowers manufacturers with smart production planning, inventory control, and real-time KPIs." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="flex-1 text-left">
            <h1 className="text-4xl font-bold mb-4">Smart ERP for Smart Manufacturing</h1>
            <p className="mb-6 text-sm sm:text-base">
              RedERP transforms manufacturing by connecting operations, inventory, production plans, and financials into one intelligent dashboard.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/manufacturing-industry.png"
              alt="Manufacturing Industry Illustration"
              className="w-[400px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold text-primary mb-3">Key Modules for Manufacturers</h2>
          <p className="text-gray-600">
            Whether you’re managing production lines or procurement workflows, RedERP’s unified system ensures full visibility and efficiency.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left max-w-6xl mx-auto">
          {[
            {
              icon: <FiSettings size={24} />,
              title: "Manufacturing & Production",
              desc: "Plan and schedule manufacturing orders, manage BOMs, allocate resources, and monitor shop floor performance.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Project Management",
              desc: "Track production milestones, lead times, deadlines, and resources across every manufacturing batch or project.",
            },
            {
              icon: <FiBox size={24} />,
              title: "Inventory Management",
              desc: "Manage raw materials, WIP inventory, and finished goods with barcode tracking, batch control, and auto-replenishment.",
            },
            {
              icon: <FiTruck size={24} />,
              title: "Procurement & Supply Chain",
              desc: "Handle vendor relationships, compare quotes, and generate POs from MRP demands or low-stock levels.",
            },
            {
              icon: <FiUsers size={24} />,
              title: "Human Resources",
              desc: "Manage your factory workforce, shift planning, attendance tracking, payroll, and productivity reports.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "BI & Reporting",
              desc: "Visualize production KPIs, downtime analytics, material costs, wastage ratios, and gross margins.",
            },
          ].map((mod, index) => (
            <div key={index} className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-3 text-primary mb-3">
                {mod.icon}
                <h3 className="font-bold">{mod.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{mod.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10 py-16">
          <div className="flex-1 text-left">
            <h3 className="text-3xl font-bold mb-4">Maximize Factory Efficiency with RedERP</h3>
            <p className="mb-6 text-sm sm:text-base">
              Start your 7-day free trial — no credit card required. Only $5/month for unlimited users and all modules.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Try RedERP for Manufacturing
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/cta2.png"
              alt="Manufacturing CTA"
              className="w-[360px] object-contain"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustryManufacturing;
